import "./index.css";
import { useState } from "react";
import { Modal, Form } from "react-bootstrap";

import CommonButton from "../../components/commonButton";
import withModal from "../../components/withModal";
import { useToast } from "../../context/TostContext";

/**
 * Add many map files to the location currently open.
 *
 * Kept separate from AddStateModal rather than folded into it: that component
 * already serves four jobs — add location, add file, edit file, view file — and
 * threading a multi-file mode through it would put the edit and view paths at
 * risk for no gain.
 *
 * The server decides per file whether to create or replace, and refuses to
 * replace anything already georeferenced. So this screen's job is to collect
 * files and titles, then show what came back — including the rows that did not
 * land, which is the part an operator actually needs.
 */

// Matches the server's default MAP_BULK_MAX_FILES / MAP_BULK_MAX_FILE_SIZE.
// Checked here too so an over-limit batch is caught before it is uploaded, but
// the server stays the authority — these are a courtesy, not the rule.
const MAX_FILES = 15;
const MAX_FILE_SIZE = 50 * 1024 * 1024;

const ALLOWED_TYPES = [
  "image/jpeg",
  "image/png",
  "image/jpg",
  "image/webp",
  "application/pdf",
];

const titleFromFilename = (name) => {
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(0, dot) : name;
};

const BulkAddFilesModalInner = ({ onClose, onSubmit }) => {
  const toast = useToast();

  // { file, title, key } — key survives reordering and removal so React does
  // not reuse a row's edited title for a different file.
  const [rows, setRows] = useState([]);
  const [result, setResult] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const addFiles = (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = "";

    if (picked.length === 0) return;

    const accepted = [];
    const rejected = [];

    for (const file of picked) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        rejected.push(`${file.name} is not an image or PDF`);
        continue;
      }

      if (file.size > MAX_FILE_SIZE) {
        rejected.push(
          `${file.name} is ${(file.size / (1024 * 1024)).toFixed(1)} MB — the limit is ${MAX_FILE_SIZE / (1024 * 1024)} MB`
        );
        continue;
      }

      accepted.push({
        key: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
        file,
        title: titleFromFilename(file.name),
      });
    }

    rejected.forEach((message) => toast.error(message));

    setRows((current) => {
      const next = [...current, ...accepted];

      if (next.length > MAX_FILES) {
        toast.error(`You can upload ${MAX_FILES} files at a time`);
        return next.slice(0, MAX_FILES);
      }

      return next;
    });
  };

  const updateTitle = (key, title) => {
    setRows((current) =>
      current.map((row) => (row.key === key ? { ...row, title } : row))
    );
  };

  const removeRow = (key) => {
    setRows((current) => current.filter((row) => row.key !== key));
  };

  /**
   * Two files can easily produce one title — the same sheet name from two
   * folders. The server catches this, but catching it here means the operator
   * fixes it before waiting for an upload.
   */
  const duplicateTitles = (() => {
    const seen = new Map();
    const clashes = new Set();

    for (const row of rows) {
      const key = row.title.trim().toLowerCase();
      if (!key) continue;

      if (seen.has(key)) clashes.add(key);
      seen.set(key, true);
    }

    return clashes;
  })();

  const blankTitles = rows.some((row) => !row.title.trim());
  const canSubmit =
    rows.length > 0 && !blankTitles && duplicateTitles.size === 0 && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    try {
      setIsSubmitting(true);

      const report = await onSubmit({
        files: rows.map((row) => row.file),
        titles: rows.map((row) => row.title.trim()),
      });

      // A refused request (limit, bad location) returns nothing to report; the
      // caller has already shown the error.
      if (!report) return;

      setResult(report);

      // Rows that landed are done with. Anything that failed stays on screen
      // with its reason, so the title can be corrected and just those retried.
      const failedNames = new Set((report.failed || []).map((row) => row.file_name));
      setRows((current) => current.filter((row) => failedNames.has(row.file.name)));
    } catch (error) {
      console.error("Bulk upload failed:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const landed = result ? (result.created?.length || 0) + (result.replaced?.length || 0) : 0;

  return (
    <>
      <Modal.Body>
        {/* ------------------------------------------------------ the result */}
        {result && (
          <div className="tp-bulk-result mb-3">
            {landed > 0 && (
              <div className="tp-bulk-summary tp-bulk-summary--ok">
                {result.created?.length > 0 && <span>{result.created.length} added</span>}
                {result.replaced?.length > 0 && (
                  <span>{result.replaced.length} replaced</span>
                )}
              </div>
            )}

            {result.failed?.length > 0 && (
              <div className="tp-bulk-summary tp-bulk-summary--fail">
                <strong>{result.failed.length} could not be uploaded</strong>
                <ul className="tp-bulk-fail-list">
                  {result.failed.map((row, index) => (
                    <li key={`${row.file_name}-${index}`}>
                      <span className="tp-bulk-fail-name">{row.file_name}</span>
                      <span className="tp-bulk-fail-why">{row.message}</span>
                    </li>
                  ))}
                </ul>
                <p className="tp-bulk-hint">
                  Fix the titles below and upload again — the rest have been saved.
                </p>
              </div>
            )}
          </div>
        )}

        {/* ------------------------------------------------------ the picker */}
        <Form.Group className="mb-3">
          <Form.Label>
            Files <span className="text-danger">*</span>
          </Form.Label>

          <div className="tp-file-row">
            <div className="tp-file-choose">Choose Files</div>

            <div className="tp-file-name">
              {rows.length > 0
                ? `${rows.length} file${rows.length === 1 ? "" : "s"} selected`
                : "No files chosen"}
            </div>

            <input
              type="file"
              className="tp-file-input"
              accept="image/*,application/pdf"
              multiple
              onChange={addFiles}
              disabled={isSubmitting}
            />
          </div>

          <Form.Text className="text-muted">
            Up to {MAX_FILES} files, {MAX_FILE_SIZE / (1024 * 1024)} MB each. Titles are
            taken from the filenames — edit any that need it.
          </Form.Text>
        </Form.Group>

        {/* -------------------------------------------------------- the rows */}
        {rows.length > 0 && (
          <div className="tp-bulk-rows">
            {rows.map((row) => {
              const trimmed = row.title.trim();
              const isDuplicate = duplicateTitles.has(trimmed.toLowerCase());
              const isBlank = !trimmed;

              return (
                <div className="tp-bulk-row" key={row.key}>
                  <div className="tp-bulk-file" title={row.file.name}>
                    {row.file.name}
                  </div>

                  <div className="tp-bulk-title">
                    <Form.Control
                      type="text"
                      value={row.title}
                      placeholder="Enter file title"
                      onChange={(event) => updateTitle(row.key, event.target.value)}
                      disabled={isSubmitting}
                      isInvalid={isDuplicate || isBlank}
                      className="shadow-none"
                    />
                    {isDuplicate && (
                      <Form.Control.Feedback type="invalid">
                        Another file in this upload has the same title
                      </Form.Control.Feedback>
                    )}
                    {isBlank && (
                      <Form.Control.Feedback type="invalid">
                        Title is required
                      </Form.Control.Feedback>
                    )}
                  </div>

                  <button
                    type="button"
                    className="tp-bulk-remove"
                    onClick={() => removeRow(row.key)}
                    disabled={isSubmitting}
                    aria-label={`Remove ${row.file.name}`}
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="justify-content-center">
        {landed > 0 && rows.length === 0 ? (
          <CommonButton
            title="Done"
            className="btn-primary fixed-size add-btn"
            onClick={onClose}
          />
        ) : (
          <CommonButton
            title={
              isSubmitting
                ? `Uploading ${rows.length}…`
                : `Upload ${rows.length || ""} ${rows.length === 1 ? "file" : "files"}`.trim()
            }
            className="btn-primary fixed-size add-btn"
            onClick={handleSubmit}
            disabled={!canSubmit}
          />
        )}
      </Modal.Footer>
    </>
  );
};

const BulkAddFilesModal = withModal(BulkAddFilesModalInner);
export default BulkAddFilesModal;
